import { Controller } from "@hotwired/stimulus";

/**
 * Messages List Controller
 *
 * Handles scroll behavior for the assistant messages list:
 * - Scrolls to the last message when a new message is added
 * - Shows scroll buttons when user scrolls away from top/bottom
 * - Debounces scroll during streaming to avoid jank
 *
 * The scroll strategy is simple:
 * 1. When a NEW message is added (user question) -> scroll immediately to show it at top
 * 2. During streaming (content changes) -> scroll only if user was at bottom
 * 3. User scrolls manually -> don't interfere, show scroll buttons
 */
export default class extends Controller {
  static targets = ["wrapper", "messages", "newContentButton", "topButton"]
  static values = {
    context: String
  }

  initialize() {
    this.isScrolledToBottom = true;
    this.isScrolledFromTop = false;
    this.lastMessageCount = 0;
    this.userHasScrolled = false;
    this._scrollHandler = () => this._handleScroll();

    // Observe DOM changes in the messages container
    this.observer = new MutationObserver((mutationList) => {
      this._handleMutations(mutationList);
    });
  }

  connect() {
    window.addEventListener('scroll', this._scrollHandler, { passive: true });

    // Only scroll on initial load, not on reconnect (e.g., turbo-permanent navigation)
    if (!this.element.dataset.messagesListConnected) {
      this.element.dataset.messagesListConnected = "true"
      this._scrollToLastMessageEnd();
      this._handleScroll();
    } else if (this.hasWrapperTarget && this.element.dataset.messagesListScrollTop) {
      // Restore scroll position after turbo-permanent reattachment
      const savedScroll = parseInt(this.element.dataset.messagesListScrollTop, 10);
      requestAnimationFrame(() => {
        this.wrapperTarget.scrollTop = savedScroll;
        this._handleScroll();
      });
    }
    this._handleSendToEditorButton();
  }

  disconnect() {
    this.observer.disconnect();
    window.removeEventListener('scroll', this._scrollHandler);
  }

  // Target lifecycle callbacks — handle re-initialization after turbo_stream.replace
  messagesTargetConnected(element) {
    this.observer.disconnect();
    this.observer.observe(element, {
      childList: true,
      subtree: true,
      characterData: true
    });
    this.lastMessageCount = this._getMessageCount();
    this._scrollToLastMessageEnd();
    this._handleSendToEditorButton();
  }

  messagesTargetDisconnected() {
    this.observer.disconnect();
  }

  wrapperTargetConnected(element) {
    element.addEventListener('scroll', this._scrollHandler, { passive: true });
    this._handleScroll();
  }

  wrapperTargetDisconnected(element) {
    element.removeEventListener('scroll', this._scrollHandler);
  }

  // Public action for scroll to bottom button
  scrollToBottomClick() {
    this.userHasScrolled = false;
    this._scrollToLastMessageEnd(true); // smooth scroll when user clicks
  }

  // Public action for scroll to top button
  scrollToTopClick() {
    this._scrollToTop();
  }

  // --- Private Methods ---

  _handleMutations(mutationList) {
    const currentCount = this._getMessageCount();
    const isNewMessage = currentCount > this.lastMessageCount;

    if (isNewMessage) {
      // A new message was added - scroll to user's question (second-to-last)
      this.lastMessageCount = currentCount;
      this.userHasScrolled = false;
      this._scrollToUserMessage();
    } else if (this.hasWrapperTarget && this.element.dataset.messagesListScrollTop) {
      // Re-rendering streamed content replaces the browser's scroll anchor.
      // Keep the viewport at the position recorded before the DOM changed.
      this.wrapperTarget.scrollTop = parseInt(this.element.dataset.messagesListScrollTop, 10);
    }
    // During streaming, don't auto-scroll - stay at the user's message

    this._toggleScrollButtons();
    this._handleSendToEditorButton();
  }

  _getMessageCount() {
    if (!this.hasMessagesTarget) return 0;
    // Count only user messages to detect when user posts a new message
    return this.messagesTarget.querySelectorAll('[data-role="user"]').length;
  }

  _handleScroll() {
    if (!this.hasWrapperTarget) return;

    const wrapper = this.wrapperTarget;
    // Continuously save scroll position so it survives turbo-permanent detach
    this.element.dataset.messagesListScrollTop = wrapper.scrollTop;
    this.isScrolledFromTop = wrapper.scrollTop > 100;

    const wasAtBottom = this.isScrolledToBottom;
    const distanceFromBottom = wrapper.scrollHeight - wrapper.scrollTop - wrapper.clientHeight;
    this.isScrolledToBottom = distanceFromBottom < 50;

    // Detect manual scroll by user
    if (wasAtBottom && !this.isScrolledToBottom) {
      this.userHasScrolled = true;
    }

    this._toggleScrollButtons();
  }

  _toggleScrollButtons() {
    if (this.hasNewContentButtonTarget) {
      const show = !this.isScrolledToBottom;
      this.newContentButtonTarget.classList.toggle('opacity-0', !show);
      this.newContentButtonTarget.classList.toggle('pointer-events-none', !show);
      this.newContentButtonTarget.classList.toggle('opacity-100', show);
      this.newContentButtonTarget.classList.toggle('pointer-events-auto', show);
    }

    if (this.hasTopButtonTarget) {
      const show = this.isScrolledFromTop && !this._isStickyProposalActive();
      this.topButtonTarget.classList.toggle('opacity-0', !show);
      this.topButtonTarget.classList.toggle('pointer-events-none', !show);
      this.topButtonTarget.classList.toggle('opacity-100', show);
      this.topButtonTarget.classList.toggle('pointer-events-auto', show);
      this._positionTopButtonBelowBanner();
    }
  }

  // A shared-conversation banner is sticky at the top of the scroll area, right
  // where the scroll-to-top button lives. Offset the button so it sits just
  // below the banner instead of overlapping it. Using the banner's viewport
  // bottom works across layout contexts because the button is position: fixed.
  _positionTopButtonBelowBanner() {
    const banner = this.element.querySelector('[data-shared-banner]');
    if (!banner) return;

    const gap = 8;
    this.topButtonTarget.style.top = `${banner.getBoundingClientRect().bottom + gap}px`;
  }

  // Returns true when a redactor-proposal sticky header is currently pinned
  // at the top of its scroll container. The scroll-to-top button shares that
  // same spot, so we hide it to avoid the visual overlap with "Insertar".
  // The sticky pins to the wrapper's scrollport (content-box), so the edge
  // includes the wrapper's border-top + padding-top.
  _isStickyProposalActive() {
    if (!this.hasMessagesTarget) return false;

    const containers = this.messagesTarget.querySelectorAll('[data-editor-target="container"]');
    if (containers.length === 0) return false;

    let stickyEdge = 0;
    if (this.hasWrapperTarget) {
      const wrapperRect = this.wrapperTarget.getBoundingClientRect();
      const paddingTop = parseFloat(window.getComputedStyle(this.wrapperTarget).paddingTop) || 0;
      stickyEdge = wrapperRect.top + this.wrapperTarget.clientTop + paddingTop;
    }

    for (const container of containers) {
      const rect = container.getBoundingClientRect();
      const pinnedAt = stickyEdge + (parseFloat(window.getComputedStyle(container).top) || 0);
      if (rect.top <= pinnedAt + 1.5 && rect.bottom > pinnedAt) return true;
    }
    return false;
  }

  _scrollToUserMessage() {
    if (!this.hasWrapperTarget || !this.hasMessagesTarget) return;

    const userMessages = this.messagesTarget.querySelectorAll('[data-role="user"]');
    if (userMessages.length === 0) return;

    const lastUserMessage = userMessages[userMessages.length - 1];

    // Use wrapper.scrollTo instead of scrollIntoView to avoid scrolling
    // parent containers (e.g., tab panel with overflow-scroll in sidebar)
    requestAnimationFrame(() => {
      if (!this.hasWrapperTarget) return;
      const offset = this._offsetRelativeToWrapper(lastUserMessage);
      this.wrapperTarget.scrollTo({ top: offset, behavior: 'smooth' });
    });
  }

  _scrollToLastMessageEnd(smooth = false) {
    if (!this.hasWrapperTarget) return;

    requestAnimationFrame(() => {
      if (!this.hasWrapperTarget) return;
      this.wrapperTarget.scrollTo({
        top: this.wrapperTarget.scrollHeight,
        behavior: smooth ? 'smooth' : 'instant'
      });
    });
  }

  _offsetRelativeToWrapper(element) {
    let offset = 0;
    let el = element;
    while (el && el !== this.wrapperTarget) {
      offset += el.offsetTop;
      el = el.offsetParent;
    }
    return offset;
  }

  _scrollToBottom() {
    if (this.hasWrapperTarget) {
      this.wrapperTarget.scrollTo({
        top: this.wrapperTarget.scrollHeight,
        behavior: 'smooth'
      });
    }
  }

  _scrollToTop() {
    if (this.hasWrapperTarget) {
      this.wrapperTarget.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  _handleSendToEditorButton() {
    if (this.contextValue !== "tools" || !this.hasMessagesTarget) return;

    const containers = this.messagesTarget.querySelectorAll('div.js-send-to-editor');
    containers.forEach(container => {
      container.classList.add('inline');
      container.classList.remove('hidden');
    });
  }
}
